export class LoggingAnalyticsHandler {
  trackPageView(url) {
    console.log('Analytics page view:', url);
  }
  trackEvent(eventName, params) {
    console.log('Analytics event:', eventName, params);
  }
}

// Listing ids are UUIDs. Matching the shape rather than "any segment" keeps
// /l/new and the draft/edit variants from being reported as ViewContent.
const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

// Route -> Meta standard event, for the events that genuinely ARE a page view.
//
// Note the listing pattern has no slug in it. canonicalRoutePath (util/routes.js)
// strips the slug from ListingPage URLs, so /l/some-slug/<id> reaches analytics
// as /l/<id>.
//
// Events that represent a form being submitted are NOT here - they are fired
// from Redux success actions in analytics.js. Viewing /make-offer or /signup is
// not the same as completing it, and a page-view trigger would count every
// visitor who opened the form and left.
const ROUTE_EVENTS = [
  { pattern: new RegExp(`^/l/(${UUID})/?$`), event: 'ViewContent' },
  { pattern: /^\/s(\/[^/]+)?\/?$/, event: 'Search' },
];

// canonicalPath carries the query string and hash (again, see canonicalRoutePath).
const pathOnly = canonicalPath => (canonicalPath || '').split(/[?#]/)[0];

const routeEventFor = canonicalPath => {
  const path = pathOnly(canonicalPath);
  const match = ROUTE_EVENTS.find(({ pattern }) => pattern.test(path));
  if (!match) {
    return null;
  }
  // content_ids/content_type let Meta tie the view to a specific listing, which
  // is what makes retargeting audiences possible.
  const listingId = path.match(new RegExp(`^/l/(${UUID})`))?.[1];
  const params = listingId ? { content_ids: [listingId], content_type: 'product' } : {};
  return { event: match.event, params };
};

// Meta (Facebook) Pixel. The base code is server-rendered into <head> by
// server/renderer.js, with util/includeScripts.js as a dev-server fallback.
export class MetaPixelHandler {
  trackEvent(eventName, params = {}) {
    if (!window.fbq) {
      return;
    }
    const { eventID, ...eventParams } = params;
    if (eventID) {
      window.fbq('track', eventName, eventParams, { eventID });
    } else {
      window.fbq('track', eventName, eventParams);
    }
  }

  trackPageView(canonicalPath, previousPath) {
    if (!window.fbq) {
      return;
    }
    const routeEvent = routeEventFor(canonicalPath);

    if (!previousPath) {
      // Initial page load. The base code in <head> already sent PageView, but a
      // standard event for the landing route still has to be sent - most ad
      // traffic lands directly on a listing page and never navigates in-app, so
      // skipping this would lose the majority of ViewContent events.
      if (routeEvent) {
        this.trackEvent(routeEvent.event, routeEvent.params);
      }
      return;
    }

    // In-app (SPA) navigation. Without this, a visitor browsing several
    // listings is recorded as a single PageView.
    //
    // Note: fbq reads the current URL itself, and the location change happens
    // before the new page renders - hence the same timeout that the GA handler
    // uses, so the URL and title have settled before the event is sent.
    window.setTimeout(() => {
      window.fbq('track', 'PageView');
      if (routeEvent) {
        this.trackEvent(routeEvent.event, routeEvent.params);
      }
    }, 300);
  }
}

// Google Analytics 4 (GA4) using gtag.js script, which is included in util/includeScripts.js
export class GoogleAnalyticsHandler {
  trackPageView(canonicalPath, previousPath) {
    // GA4 property. Manually send page_view events
    // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
    // Note 1: You should turn "Enhanced measurement" off.
    //         It attaches own listeners to elements and that breaks in-app navigation.
    // Note 2: If previousPath is null (just after page load), gtag script sends page_view event automatically.
    //         Only in-app navigation needs to be sent manually from SPA.
    // Note 3: Timeout is needed because gtag script picks up <title>,
    //         and location change event happens before initial rendering.
    if (previousPath && window.gtag) {
      window.setTimeout(() => {
        window.gtag('event', 'page_view', {
          page_path: canonicalPath,
        });
      }, 300);
    }
  }
}
